import {
    RequirementKind,
    RequirementStatus,
    SectionRequirement,
    SectionRequirementsData,
} from '@/api/ai';
import { ClinicalHistoryStatus } from '@/api/reports';
import { normalizeRichTextValue } from '@/lib/rich-text';
import { ReportData } from '@/types/report';
import { SECTIONS } from './constants';
import { Sections } from './types';

// ---------------------------------------------------------------------------
// Core types
// ---------------------------------------------------------------------------

export type SourceAvailability = 'available' | 'pending' | 'processing' | 'failed';

type SourceResolutionContext = {
    stepId?: Sections;
    clinicalHistoryStatus?: ClinicalHistoryStatus;
    clinicalHistoryReady?: boolean;
};

type SourceDescriptor = {
    id: string;
    label: string;
    required: boolean;
    kind: RequirementKind;
    getAvailability: (data: ReportData, context?: SourceResolutionContext) => SourceAvailability;
};

/** Shape returned by resolved source functions (no getAvailability fn). */
export type ResolvedSource = {
    id: string;
    label: string;
    required: boolean;
    /** Whether a satisfied source keeps its place in the panel. See RequirementKind. */
    kind: RequirementKind;
    availability: SourceAvailability;
    /**
     * Whether the document itself is on file — as opposed to something derived
     * from it being ready.
     *
     * On the História Clínica step `availability` answers a wider question: it
     * goes `available` when the derived history is ready, which stays true if
     * the anamnesis is deleted afterwards. Showing the document row off that
     * would claim a file that is gone. `undefined` while the lookup is in
     * flight, so the row does not flicker in and out on every step change.
     */
    present?: boolean;
    /**
     * Whether this source is what stands between the user and the CTA, *and*
     * the user is the one who can clear it.
     *
     * Narrower than `required && !available`: a source the system is still
     * preparing, or one whose preparation failed, also holds the CTA down, but
     * neither is a to-do. The panel counts blockers and turns each into a
     * button, so folding those two in would offer a button for work nobody can
     * do. `isSourceHoldingCta` is the wider question the CTA itself asks.
     */
    blocking: boolean;
};

export type RequiredSourcesStatus = {
    totalRequired: number;
    availableRequired: number;
    missingRequired: number;
    hasMissingRequired: boolean;
    hasProcessingRequired: boolean;
    hasFailedRequired: boolean;
};

// ---------------------------------------------------------------------------
// Shared availability helpers
// ---------------------------------------------------------------------------

// The anamnesis lives on the patient and has no processing lifecycle anymore:
// it either exists (typed or uploaded, extracted synchronously) or it doesn't.
const hasAnamnesis = (data: ReportData) => Boolean(data.has_anamnesis);

const getAnamnesisSourceAvailability = (
    data: ReportData,
    context?: SourceResolutionContext,
): SourceAvailability => {
    if (isClinicalHistoryStep(context)) {
        if (context?.clinicalHistoryReady || context?.clinicalHistoryStatus === 'ready') {
            return 'available';
        }

        if (context?.clinicalHistoryStatus === 'failed') {
            return 'failed';
        }

        if (isClinicalHistoryProcessing(context)) {
            return 'processing';
        }
    }

    return hasAnamnesis(data) ? 'available' : 'pending';
};

const hasMeaningfulContent = (value?: string) =>
    Boolean(normalizeRichTextValue(value || '').trim());

const isClinicalHistoryProcessing = (context?: SourceResolutionContext) =>
    Boolean(
        !context?.clinicalHistoryReady &&
        (context?.clinicalHistoryStatus === 'queued' ||
            context?.clinicalHistoryStatus === 'building'),
    );

const isClinicalHistoryStep = (context?: SourceResolutionContext) =>
    context?.stepId === SECTIONS.CLINICAL_HISTORY;

const hasUsableTableData = (data: ReportData) => {
    const blocks = data.tests?.blocks || [];
    const tableBlocks = blocks.filter((block) => block.block_type === 'table');

    return tableBlocks.some((tableBlock) => {
        const headerValues = tableBlock.header?.cells || [];
        const rowValues = tableBlock.rows?.flatMap((row) => row.cells) || [];
        const values = [...headerValues, ...rowValues];

        return values.some((cell) => (cell.value || '').trim().length > 0);
    });
};

// ---------------------------------------------------------------------------
// Source registry — frontend-specific overrides keyed by source key.
//
// Sources listed here have a local `getAvailability` that runs in real-time
// against the in-memory ReportData, giving the user instant feedback without
// waiting for a save round-trip. Sources not listed use the backend status.
//
// The two message fields carry copy the panel cannot derive: "preparing" and
// "this failed" are about a specific background job, and only the source knows
// which one. Without them the panel would have to say something generic about
// a named row, which is how the old status vocabulary started.
// ---------------------------------------------------------------------------

type SourceRegistryEntry = {
    /** Full sentence shown while the source is being prepared. */
    waitingMessage?: string;
    /** Full sentence shown when preparation failed. */
    failedMessage?: string;
    getAvailability?: (data: ReportData, context?: SourceResolutionContext) => SourceAvailability;
};

// Both keys below are fed by the same background job, so they say the same
// thing when it is running or has failed.
const CLINICAL_HISTORY_WAITING = 'Processando a anamnese';
const CLINICAL_HISTORY_FAILED = 'Não foi possível preparar a história clínica.';

const SOURCE_REGISTRY: Record<string, SourceRegistryEntry> = {
    anamnesis: {
        getAvailability: getAnamnesisSourceAvailability,
        waitingMessage: CLINICAL_HISTORY_WAITING,
        failedMessage: CLINICAL_HISTORY_FAILED,
    },
    test_table_block: {
        getAvailability: (data) => (hasUsableTableData(data) ? 'available' : 'pending'),
    },
    clinical_history_content: {
        waitingMessage: CLINICAL_HISTORY_WAITING,
        failedMessage: CLINICAL_HISTORY_FAILED,
        getAvailability: (data, context) => {
            if (hasMeaningfulContent(data.clinical_history?.content)) {
                return 'available';
            }

            if (isClinicalHistoryProcessing(context)) {
                return 'processing';
            }

            if (context?.clinicalHistoryStatus === 'ready') {
                return 'available';
            }

            if (context?.clinicalHistoryStatus === 'failed') {
                return 'failed';
            }

            return 'pending';
        },
    },
    diagnosis_content: {
        getAvailability: (data) =>
            hasMeaningfulContent(data.diagnosis?.content) ? 'available' : 'pending',
    },
    tests_results: {
        getAvailability: (data) =>
            (data.tests?.blocks || []).length > 0 ? 'available' : 'pending',
    },
    patient_date_of_birth: {
        getAvailability: (data) => (data.patient?.date_of_birth ? 'available' : 'pending'),
    },
};

/** The sentence a non-actionable state shows, or undefined if there is none. */
export function getSourceMessage(source: ResolvedSource): string | undefined {
    const registry = SOURCE_REGISTRY[source.id];

    if (source.availability === 'processing') return registry?.waitingMessage;
    if (source.availability === 'failed') return registry?.failedMessage;

    return undefined;
}

// ---------------------------------------------------------------------------
// Backend status → frontend availability mapping
// ---------------------------------------------------------------------------

function mapBackendStatus(status: RequirementStatus): SourceAvailability {
    return status === 'ready' ? 'available' : 'pending';
}

/** A source that no longer stands in the way of a generation. */
export function isSourceSatisfied(source: { availability: SourceAvailability }): boolean {
    return source.availability === 'available';
}

/**
 * Whether a source is keeping the CTA down, for any reason.
 *
 * `blocking` answers "is there something for the user to do?"; this answers
 * "can the button run?". They differ exactly on `processing` and `failed`,
 * which hold the CTA without being to-dos.
 */
export function isSourceHoldingCta(source: ResolvedSource): boolean {
    return source.required && !isSourceSatisfied(source);
}

function annotate(sources: Omit<ResolvedSource, 'blocking'>[]): ResolvedSource[] {
    return sources.map((source) => ({
        ...source,
        blocking: source.required && source.availability === 'pending',
    }));
}

// ---------------------------------------------------------------------------
// Merge: backend requirements + local real-time resolution
// ---------------------------------------------------------------------------

function buildSectionSources(
    backendRequirements: SectionRequirement[],
    data: ReportData,
    context?: SourceResolutionContext,
): ResolvedSource[] {
    return annotate(
        backendRequirements.map((req) => {
            const registry = SOURCE_REGISTRY[req.key];

            // Frontend resolver takes priority for real-time feedback.
            // Otherwise fall back to backend-provided status.
            const availability = registry?.getAvailability
                ? registry.getAvailability(data, context)
                : mapBackendStatus(req.status);

            return {
                id: req.key,
                label: req.alias,
                required: req.required,
                kind: req.kind,
                availability,
            };
        }),
    );
}

// ---------------------------------------------------------------------------
// Public API — resolved sources (backend-driven with frontend fallback)
// ---------------------------------------------------------------------------

/**
 * Returns resolved sources for a section, merging backend requirements with
 * frontend real-time availability checks.
 *
 * When `backendRequirements` is available, the backend drives which sources
 * exist, their labels, whether they are required and what kind they are. The
 * frontend registry provides real-time availability for sources it can check
 * locally.
 *
 * When `backendRequirements` is not available (loading / error), falls back
 * to the hardcoded `SECTION_SOURCE_MAP`.
 */
export function getResolvedSectionSources(
    stepId: Sections,
    backendRequirements: SectionRequirementsData | undefined,
    data: ReportData,
    context?: SourceResolutionContext,
): ResolvedSource[] {
    const sectionReqs = backendRequirements?.[stepId];

    if (sectionReqs) {
        return buildSectionSources(sectionReqs, data, context);
    }

    // Fallback: use hardcoded source map
    return getSectionSourcesWithAvailability(stepId, data, context);
}

/**
 * Computes required-sources status from resolved sources.
 *
 * Every required source must be satisfied on its own — the OR groups went out
 * with the pré-anamnese forms, which were the only case where two sources
 * could cover for each other.
 */
export function getResolvedRequiredSourcesStatus(
    resolvedSources: ResolvedSource[],
): RequiredSourcesStatus {
    const requiredSources = resolvedSources.filter((source) => source.required);

    const availableRequired = requiredSources.filter(isSourceSatisfied).length;
    // Only `pending` counts as missing. The three flags are meant to be read
    // as separate reasons the CTA is down, and folding a source the system is
    // still preparing into "missing" made two of them true at once for one
    // source — so `available + missing` deliberately does not have to equal
    // `total`.
    const missingRequired = requiredSources.filter(
        (source) => source.availability === 'pending',
    ).length;

    return {
        totalRequired: requiredSources.length,
        availableRequired,
        missingRequired,
        hasMissingRequired: missingRequired > 0,
        hasProcessingRequired: requiredSources.some(
            (source) => source.availability === 'processing',
        ),
        hasFailedRequired: requiredSources.some((source) => source.availability === 'failed'),
    };
}

/** DOM id of a source row, so the CTA can point `aria-describedby` at the row
    that is holding it back instead of repeating it in a sentence. */
export const getSourceRowId = (stepId: string, sourceId: string) =>
    `ai-source-${stepId}-${sourceId}`;

/**
 * The row a blocked CTA should refer to: the first source holding it down, in
 * the order the panel renders them. Failures come first because they are the
 * only state that reports something went wrong.
 */
export function getFirstBlockingSource(sources: ResolvedSource[]): ResolvedSource | undefined {
    const holding = sources.filter(isSourceHoldingCta);

    return (
        holding.find((source) => source.availability === 'failed') ??
        holding.find((source) => source.availability === 'processing') ??
        holding[0]
    );
}

// ---------------------------------------------------------------------------
// Legacy API — hardcoded fallback (kept for backward compatibility)
// ---------------------------------------------------------------------------

const SECTION_SOURCE_MAP: Partial<Record<Sections, SourceDescriptor[]>> = {
    [SECTIONS.DEMAND_DESCRIPTION]: [
        {
            id: 'anamnesis',
            // Not "Anamnese enviada": that bakes a state into the noun and
            // reads as a lie on a row where nothing has been sent. Matches
            // the API alias this path stands in for.
            label: 'Anamnese',
            required: true,
            kind: 'document',
            getAvailability: getAnamnesisSourceAvailability,
        },
    ],
    [SECTIONS.CLINICAL_HISTORY]: [
        {
            id: 'anamnesis',
            label: 'Anamnese',
            required: true,
            kind: 'document',
            getAvailability: getAnamnesisSourceAvailability,
        },
    ],
    [SECTIONS.DIAGNOSIS]: [
        {
            id: 'clinical_history_content',
            label: 'História Clínica preenchida',
            required: false,
            kind: 'prerequisite',
            getAvailability: (data) =>
                hasMeaningfulContent(data.clinical_history?.content) ? 'available' : 'pending',
        },
        {
            id: 'tests_results',
            label: 'Resultados de instrumentos no laudo',
            required: false,
            kind: 'prerequisite',
            getAvailability: (data) =>
                (data.tests?.blocks || []).length > 0 ? 'available' : 'pending',
        },
    ],
    [SECTIONS.RECOMMENDATIONS]: [
        {
            id: 'clinical_history_content',
            label: 'História Clínica preenchida',
            required: true,
            kind: 'prerequisite',
            getAvailability: (data) =>
                hasMeaningfulContent(data.clinical_history?.content) ? 'available' : 'pending',
        },
        {
            id: 'diagnosis_content',
            label: 'Conclusão preenchida',
            required: true,
            kind: 'prerequisite',
            getAvailability: (data) =>
                hasMeaningfulContent(data.diagnosis?.content) ? 'available' : 'pending',
        },
        {
            id: 'patient_date_of_birth',
            label: 'Data de nascimento do paciente',
            required: true,
            kind: 'prerequisite',
            getAvailability: (data) => (data.patient?.date_of_birth ? 'available' : 'pending'),
        },
    ],
};

const getSectionSourcesWithAvailability = (
    stepId: Sections,
    data: ReportData,
    context?: SourceResolutionContext,
): ResolvedSource[] => {
    const descriptors = SECTION_SOURCE_MAP[stepId];

    if (!descriptors) return [];

    return annotate(
        descriptors.map((source) => ({
            id: source.id,
            label: source.label,
            required: source.required,
            kind: source.kind,
            availability: source.getAvailability(data, context),
        })),
    );
};
